import React from 'react';
import { useSelector } from 'react-redux';

import { Permission } from '@/permissions/Permission';
import { Tooltip, TrevorIcon } from '@trevor/ui-components';
import { CREDITS, MAP, REPORT, TEAM } from '@/util/enums/views.constants';
import { useDatasourceId } from '@/hooks';
import { TrevorLink } from '@/components/TrevorLink';
import {
  getDatasourceName,
  getIsAdmin,
  getIsCreditPricing,
  getIsDatasourceReady,
  getIsOwner,
  getIsSelfHosted,
  getSavedQueriesCount,
  getUserActions,
} from '@/store-selectors';
import { userIsFinishedOnboarding } from '@/components/HighlightFeature';
import { useResetLastUpdateMethod } from '@/features/query-builder-steps/queryBuilderStepsSlice';
import {
  SideBarButtonContainer,
  SideBarContainer,
  SideBarLogoContainer,
} from './MainSidebar.styles';
import { SettingsItem } from './components/SettingsItem';
import { SideBarButton } from './components/SettingsItem.styles';
import { QueryBuilderNavigationItem } from './components/QueryBuilderNavigationItem';
import { NavigationItem } from './components/NavigationItem';
import { DashboardsNavigationItem } from './components/DashboardsNavigationItem';
import { SavedQueriesNavigationItem } from './components/SavedQueriesNavigationItem';

export const MainSideBar = () => {
  const datasourceId = useDatasourceId();
  const isDatasourceReady = useSelector(getIsDatasourceReady);
  const hasSavedQueries = useSelector(getSavedQueriesCount) > 0;
  const isSelfHosted = useSelector(getIsSelfHosted);
  const isAdmin = useSelector(getIsAdmin);
  const isOwner = useSelector(getIsOwner);
  const isCreditsPricing = useSelector(getIsCreditPricing);
  const datasourceName = useSelector(getDatasourceName);
  const userActions = useSelector(getUserActions);
  const finishedOnboarding = userIsFinishedOnboarding(userActions);

  useResetLastUpdateMethod();

  // TODO: ideally we would return some kind of a 'skeleton' component instead. Needs to be clarified with design team.
  if (!isDatasourceReady) return null;

  const writeDatasourcePermission = `${datasourceId}.write`;

  return (
    <SideBarContainer>
      <div>
        <SideBarLogoContainer data-tooltip-id="title">
          <TrevorLink to="/datasources">
            <TrevorIcon name="logo-symbol-animated" height="30.32" width="24" />
          </TrevorLink>

          <Tooltip id="title" place="right">
            {datasourceName}
          </Tooltip>
        </SideBarLogoContainer>
        <SideBarButtonContainer>
          <Permission permissionPath={`${datasourceId}.queryBuilderEnabled`}>
            <QueryBuilderNavigationItem />
          </Permission>
          {finishedOnboarding && (
            <Permission permissionPath={`${datasourceId}.savedQueriesEnabled`}>
              <SavedQueriesNavigationItem />
            </Permission>
          )}

          <Permission permissionPath={`${datasourceId}.dashboardsEnabled`}>
            <DashboardsNavigationItem disabled={!hasSavedQueries} />
          </Permission>
          {finishedOnboarding && (
            <Permission permissionPath={writeDatasourcePermission}>
              <NavigationItem tab={MAP} tooltipText="Database map">
                <TrevorIcon name="compass" size="md" />
              </NavigationItem>
            </Permission>
          )}
        </SideBarButtonContainer>
      </div>
      <SideBarButtonContainer>
        {finishedOnboarding && (
          <>
            <Permission permissionPath={writeDatasourcePermission}>
              {isCreditsPricing && !isSelfHosted && (
                <NavigationItem tab={CREDITS} tooltipText="Credits">
                  <TrevorIcon name="dashboard" size="md" />
                </NavigationItem>
              )}
            </Permission>
            <span>
              <SideBarButton
                onClick={() => window.open('https://guide.trevor.io', '_blank')}
                data-tooltip-id="guideTab"
              >
                <TrevorIcon name="book-open" size="md" />
              </SideBarButton>
              <Tooltip id="guideTab" place="right">
                Guide
              </Tooltip>
            </span>
          </>
        )}
        {isAdmin && (
          <NavigationItem tab={TEAM} tooltipText="Manage team">
            <TrevorIcon name="user" size="md" />
          </NavigationItem>
        )}
        {(isAdmin || isOwner) && !isSelfHosted && finishedOnboarding ? (
          <NavigationItem tab={REPORT} tooltipText="Reports">
            <TrevorIcon name="document" size="md" />
          </NavigationItem>
        ) : null}
        <SettingsItem />
      </SideBarButtonContainer>
    </SideBarContainer>
  );
};
